import { Option, Schema as S } from "effect";
import type { Html, HtmlBuilder } from "foldkit/html";
import { defineMessageUnion } from "foldkit/message";

import type { Card, Reason, Week } from "../api/schemas/keepInTouch";
import { Route, linkHref } from "../app/routes";
import { i18next } from "../i18n";

/**
 * The week's cards (R16, R17, R21), shared by the Home strip and the Keep in
 * touch page (KTD8). The Record action is a link carrying `via=keep-in-touch`
 * so the record page can attribute the entry that follows.
 */

export const Message = defineMessageUnion({
	ClickedSkip: { recordId: S.String },
	ClickedSwap: { recordId: S.String },
	/** KTD4: each host decides what "Reach out" does (Home prefills capture; the page navigates). */
	ClickedReachOut: { recordId: S.String },
});
export type Message = typeof Message.Type;

/** Cards still to act on: a cleared card stays in the payload but leaves the strip (R13, R14). */
export const visible = (week: Week): ReadonlyArray<Card> =>
	week.cards.filter((card) => !card.cleared);

export const cadenceLabel = (cadence: NonNullable<Reason["cadence"]>): string =>
	i18next.t(`keepInTouch.cadence.${cadence}`);

/** R16: cadence and gap, the gap alone for uncadenced people, or "no contact recorded". */
export const reasonText = (reason: Reason): string => {
	const days = reason.daysSinceContact;
	if (reason.cadence === undefined) {
		return days === null
			? i18next.t("keepInTouch.reason.noContact")
			: i18next.t("keepInTouch.reason.gap", { days });
	}
	const cadence = cadenceLabel(reason.cadence);
	return days === null
		? i18next.t("keepInTouch.reason.cadencedNoContact", { cadence })
		: i18next.t("keepInTouch.reason.cadenced", { cadence, days });
};

export const recordHref = (current: Route, recordId: string): string =>
	linkHref(
		current,
		Route.Record({
			id: recordId,
			asOf: Option.none(),
			via: Option.some("keep-in-touch"),
		}),
	);

const initials = (name: string): string =>
	name
		.split(/\s+/)
		.filter((part) => part.length > 0)
		.slice(0, 2)
		.map((part) => part[0]?.toUpperCase() ?? "")
		.join("");

/** `grid` keeps the bordered card; `rows` is a hairline row with the avatar leading and actions trailing. */
const cardView = <P>(
	h: HtmlBuilder<P>,
	current: Route,
	layout: "grid" | "rows",
	toParentMessage: (message: Message) => P,
	card: Card,
): Html => {
	const { name, id } = card.record;
	const latest = card.context.latestEntry;
	const rows = layout === "rows";
	const overdue = card.reason.overdue ? "text-secondary" : "";
	return h.keyed("div")(
		id,
		[
			h.Class(
				rows
					? "flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-base-300 py-4 last:border-b sm:flex-nowrap"
					: "card card-border bg-base-100 p-3 flex flex-col gap-1",
			),
		],
		[
			rows
				? h.div(
						[
							h.AriaHidden(true),
							h.Class(
								"grid size-10 shrink-0 place-items-center rounded-full bg-base-200 font-display text-sm text-base-content/70",
							),
						],
						[initials(name)],
					)
				: h.empty,
			h.div(
				[h.Class(rows ? "min-w-0 grow" : "contents")],
				[
					h.a(
						[
							h.Href(recordHref(current, id)),
							h.Class(
								rows
									? "font-display text-lg font-medium hover:text-primary transition-colors"
									: "font-semibold hover:text-primary transition-colors",
							),
						],
						[name],
					),
					h.p(
						[
							h.Class(
								`text-sm ${rows ? `text-base-content/70 ${overdue}` : ""}`.trim(),
							),
						],
						[reasonText(card.reason)],
					),
					latest === undefined
						? h.empty
						: h.p(
								[
									h.Class(
										`text-sm text-base-content/70 ${rows ? "truncate" : "line-clamp-2"}`,
									),
								],
								[latest.snippet || latest.title],
							),
					card.context.openTasks === 0
						? h.empty
						: h.p(
								[h.Class("text-xs text-base-content/70")],
								[
									i18next.t("keepInTouch.card.openTasks", {
										count: card.context.openTasks,
									}),
								],
							),
				],
			),
			h.div(
				[h.Class(rows ? "flex shrink-0 gap-1.5" : "flex gap-2 mt-auto pt-1")],
				[
					h.button(
						[
							h.Type("button"),
							h.Class(
								rows ? "btn btn-sm btn-primary" : "btn btn-xs btn-primary",
							),
							h.AriaLabel(
								i18next.t("keepInTouch.card.reachOutLabel", { name }),
							),
							h.OnClick(
								toParentMessage(Message.ClickedReachOut({ recordId: id })),
							),
						],
						[i18next.t("keepInTouch.card.reachOut")],
					),
					h.button(
						[
							h.Type("button"),
							h.Class(rows ? "btn btn-sm" : "btn btn-xs"),
							h.AriaLabel(i18next.t("keepInTouch.card.swapLabel", { name })),
							h.OnClick(toParentMessage(Message.ClickedSwap({ recordId: id }))),
						],
						[i18next.t("keepInTouch.card.swap")],
					),
					h.button(
						[
							h.Type("button"),
							h.Class(rows ? "btn btn-sm btn-ghost" : "btn btn-xs btn-ghost"),
							h.AriaLabel(i18next.t("keepInTouch.card.skipLabel", { name })),
							h.OnClick(toParentMessage(Message.ClickedSkip({ recordId: id }))),
						],
						[i18next.t("keepInTouch.card.skip")],
					),
				],
			),
		],
	);
};

/** `grid` is the keep-in-touch page's three-up; `rows` is one full-width card per row (Home, R6). */
export const view = <P>(
	h: HtmlBuilder<P>,
	config: Readonly<{
		cards: ReadonlyArray<Card>;
		current: Route;
		layout: "grid" | "rows";
		toParentMessage: (message: Message) => P;
	}>,
): Html =>
	h.div(
		[
			h.DataAttribute("testid", "keep-in-touch-cards"),
			h.Class(
				config.layout === "grid"
					? "grid gap-3 sm:grid-cols-3"
					: "flex flex-col",
			),
		],
		config.cards.map((card) =>
			cardView(h, config.current, config.layout, config.toParentMessage, card),
		),
	);

/** R20: no candidates in the circle; `onAddRecord` opens the parent's add-record affordance. */
export const emptyState = <P>(h: HtmlBuilder<P>, onAddRecord: P): Html =>
	h.div(
		[h.Class("text-sm text-base-content/70 flex flex-wrap items-center gap-2")],
		[
			h.p([], [i18next.t("keepInTouch.empty.explanation")]),
			h.button(
				[
					h.Type("button"),
					h.Class("btn btn-xs"),
					h.AriaLabel(i18next.t("keepInTouch.empty.addRecord")),
					h.OnClick(onAddRecord),
				],
				[i18next.t("keepInTouch.empty.addRecord")],
			),
		],
	);
